"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { Appointment } from "@/types";
import { appointmentService } from "@/services/appointmentService";
import { AppointmentCreateInput } from "@/types/appointment.api.types";
import { transformAppointmentToApi } from "@/types/appointment.api.types";
import {
  AppoinmentInput,
  AppointmentServiceCreateInput,
} from "@/types/appoinment.types";
import { useCustomers } from "./CustomerContext";
import { useVehicles } from "./VehicleContext";
import { useSubscriptions } from "./SubscriptionContext";
import { useActivities } from "./ActivityContext";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";
import {
  AppointmentQueryParams,
  PaginationMeta,
} from "@/types/appointment.api.types";
import { paymentService } from "@/services/paymentService";

interface AppointmentContextType {
  appointments: Appointment[];
  loading: boolean;
  error: string | null;
  pagination: PaginationMeta | undefined;
  addAppointment: (
    appointment: Omit<Appointment, "id">,
  ) => Promise<Appointment>;
  updateAppointment: (
    id: string,
    appointment: Partial<Appointment>,
  ) => Promise<void>;
  deleteAppointment: (id: string) => Promise<void>;
  getAppointmentById: (id: string) => Appointment | undefined;
  fetchAppointments: () => Promise<void>;
  fetchAppointmentsWithFilters: (
    params?: AppointmentQueryParams,
  ) => Promise<void>;
  refreshAppointments: () => Promise<void>;
}

const AppointmentContext = createContext<AppointmentContextType | undefined>(
  undefined,
);

export function AppointmentProvider({ children }: { children: ReactNode }) {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta | undefined>(
    undefined,
  );
  // Store last filter params for refresh to preserve filters
  const [lastParams, setLastParams] = useState<AppointmentQueryParams>({});
  const { customers, updateCustomer } = useCustomers();
  const { vehicles, updateVehicle } = useVehicles();
  const { subscriptions, updateSubscription } = useSubscriptions();
  const { addActivity } = useActivities();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const fetchAppointments = useCallback(async () => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await appointmentService.getAllAppointments();
      setAppointments(result.appointments);
      setPagination(result.pagination);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch appointments";
      setError(errorMessage);
      console.error("Error fetching appointments:", error);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAppointmentsWithFilters = useCallback(
    async (params?: AppointmentQueryParams) => {
      // Only fetch if authenticated
      if (!isAuthenticated) {
        return;
      }

      // Store params for refresh to preserve filters
      setLastParams(params || {});

      setLoading(true);
      setError(null);
      try {
        const result = await appointmentService.getAllAppointments(params);
        setAppointments(result.appointments);
        setPagination(result.pagination);
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch appointments";
        setError(errorMessage);
        console.error("Error fetching appointments:", error);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated],
  );

  const refreshAppointments = useCallback(async () => {
    // Use stored lastParams to preserve current filters when refreshing
    await fetchAppointmentsWithFilters(lastParams);
  }, [fetchAppointmentsWithFilters, lastParams]);

  const addAppointment = useCallback(
    async (
      appointmentData: Omit<
        Appointment,
        "id" | "createdAt" | "updatedAt" | "isDeleted"
      >,
    ) => {
      // Optimistic update
      const tempId = `APT-TEMP-${Date.now()}`;
      const optimisticAppointment: Appointment = {
        ...appointmentData,
        id: tempId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isDeleted: false,
      };
      setAppointments((prev) => [optimisticAppointment, ...prev]);

      try {
        const apiInput: AppointmentCreateInput = {
          type: appointmentData.type,
          typeId: appointmentData.typeId,
          date:
            appointmentData.date instanceof Date
              ? appointmentData.date.toISOString()
              : appointmentData.date,
          status: appointmentData.status,
          paymentMode: appointmentData.paymentMode,
          amount: appointmentData.amount,
          discount: (appointmentData as any).discount || 0,
          customerId: appointmentData.customerId,
          vehicleIds: appointmentData.vehicleIds || [],
          staffId: appointmentData.staffId,
          address: appointmentData.address,
          ruleId: appointmentData.ruleId,
          remarks: appointmentData.remarks || "",
          bookingType: (appointmentData as any).bookingType,
          // Pass recurring info if present so the service can map to backend details
          recurringDay: (appointmentData as any).recurringDay,
          preferredTime: (appointmentData as any).preferredTime,
          // Include booking reference if provided
          bookingRef: (appointmentData as any).bookingRef,
          // Pass repeatedAppointmentId to link to existing setup (prevents duplicate creation)
          repeatedAppointmentId:
            (appointmentData as any).repeatedAppointmentId || null,
          // Pass subtotal override if provided
          subtotal: (appointmentData as any).subtotal,
        };
        const newAppointment =
          await appointmentService.createAppointment(apiInput);

        // Replace optimistic update with real data
        setAppointments((prev) =>
          prev.map((apt) => (apt.id === tempId ? newAppointment : apt)),
        );

        const customer = customers.find(
          (c) => c.id === appointmentData.customerId,
        );
        if (customer) {
          addActivity({
            userId: "SYSTEM",
            action: "created",
            module: "appointment",
            recordId: newAppointment.id,
            description: `New appointment scheduled for ${customer.name}`,
          });
        }

        toast.success("Appointment created successfully");
        return newAppointment;
      } catch (error: any) {
        // Rollback optimistic update
        setAppointments((prev) => prev.filter((apt) => apt.id !== tempId));
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to create appointment";
        toast.error(errorMessage);
        throw error;
      }
    },
    [addActivity, customers],
  );

  const updateAppointment = useCallback(
    async (id: string, appointmentData: Partial<Appointment>) => {
      // Store previous state for rollback
      const previousAppointment = appointments.find((a) => a.id === id);
      if (!previousAppointment) {
        throw new Error("Appointment not found");
      }

      // Optimistic update
      const updatedAppointment = { ...previousAppointment, ...appointmentData };
      setAppointments((prev) =>
        prev.map((a) => (a.id === id ? updatedAppointment : a)),
      );

      // Update vehicle last service date if appointment is completed
      if (
        appointmentData.status === "COMPLETED" &&
        previousAppointment.status !== "COMPLETED"
      ) {
        const vehicle = vehicles.find((v) =>
          updatedAppointment.vehicleIds?.includes(v.id),
        );
        if (vehicle) {
          updateVehicle(vehicle.id, {
            lastService: new Date(),
            totalServices: vehicle.totalServices + 1,
          });
        }

        // Update customer total spending and last visit
        const customer = customers.find(
          (c) => c.id === updatedAppointment.customerId,
        );
        if (customer && updatedAppointment.amount !== null) {
          updateCustomer(customer.id, {
            totalSpending: customer.totalSpending + updatedAppointment.amount,
            lastVisit: new Date(),
          });
        }
      }

      try {
        // If only status is being updated, use the status update endpoint
        if (
          appointmentData.status &&
          Object.keys(appointmentData).length === 1
        ) {
          const updatedDto = await appointmentService.updateAppointmentStatus(
            id,
            appointmentData.status,
          );
          // Update state with server response to ensure consistency
          // Preserve ALL critical fields that the backend might not return in partial updates
          setAppointments((prev) =>
            prev.map((a) =>
              a.id === id
                ? {
                    ...a, // Preserve ALL existing fields first
                    ...updatedDto, // Overlay with new data from API
                    // Explicitly preserve critical lookup/display fields if API returns null/undefined/empty
                    customerId: updatedDto.customerId || a.customerId,
                    vehicleIds: updatedDto.vehicleIds?.length
                      ? updatedDto.vehicleIds
                      : a.vehicleIds,
                    bookingRef: updatedDto.bookingRef || a.bookingRef,
                    customerName: updatedDto.customerName || a.customerName,
                    serviceName: updatedDto.serviceName || a.serviceName,
                    type: updatedDto.type || a.type,
                    typeId: updatedDto.typeId || a.typeId,
                    // Preserve amount and discount for correct display calculation
                    amount: updatedDto.amount ?? a.amount,
                    discount: updatedDto.discount ?? a.discount,
                    bookingType: updatedDto.bookingType ?? a.bookingType,
                  }
                : a,
            ),
          );
        } else {
          // Build API input for update - use any to allow all possible fields
          const apiInput: any = {};

          // Always include customerId from the existing appointment
          apiInput.customerId = previousAppointment.customerId;

          // Include updated fields
          if (appointmentData.date !== undefined) {
            apiInput.date =
              appointmentData.date instanceof Date
                ? appointmentData.date.toISOString()
                : appointmentData.date;
          }
          if (appointmentData.status !== undefined) {
            apiInput.status = appointmentData.status;
          }
          if (appointmentData.paymentMode !== undefined) {
            apiInput.paymentMode = appointmentData.paymentMode;
          }
          if (appointmentData.amount !== undefined) {
            // Note: amount is not directly updatable via API, but include for completeness
          }
          if (appointmentData.vehicleIds !== undefined) {
            apiInput.vehicleIds = appointmentData.vehicleIds || [];
          }
          if (
            appointmentData.staffId !== undefined &&
            appointmentData.staffId !== null &&
            appointmentData.staffId !== ""
          ) {
            apiInput.staffId = appointmentData.staffId;
          }
          if (appointmentData.address) {
            apiInput.address = appointmentData.address;
          }
          if (appointmentData.remarks) {
            apiInput.remarks = appointmentData.remarks;
          }
          if (appointmentData.ruleId !== undefined) {
            apiInput.ruleId = appointmentData.ruleId || null;
          }

          // Include bookingRef if provided
          if (appointmentData.bookingRef !== undefined) {
            apiInput.bookingRef = appointmentData.bookingRef;
          }

          // Include featureIds if provided (from extended appointment data)
          const extendedData = appointmentData as Partial<Appointment> & {
            featureIds?: string[];
            bookingType?: "ONLINE" | "OFFLINE" | "SUBSCRIPTION";
          };
          if (extendedData.featureIds !== undefined) {
            apiInput.featureIds = extendedData.featureIds;
          }
          if (extendedData.bookingType !== undefined) {
            apiInput.bookingType = extendedData.bookingType;
          }

          const updatedDto = await appointmentService.updateAppointment(
            id,
            apiInput,
          );
          // Update state with server response to capture calculated fields (e.g. amount)
          // Preserve ALL critical fields that the backend might not return in partial updates
          setAppointments((prev) =>
            prev.map((a) =>
              a.id === id
                ? {
                    ...a, // Preserve ALL existing fields first
                    ...updatedDto, // Overlay with new data from API
                    // Explicitly preserve critical lookup/display fields if API returns null/undefined/empty
                    customerId: updatedDto.customerId || a.customerId,
                    vehicleIds: updatedDto.vehicleIds?.length
                      ? updatedDto.vehicleIds
                      : a.vehicleIds,
                    bookingRef: updatedDto.bookingRef || a.bookingRef,
                    customerName: updatedDto.customerName || a.customerName,
                    serviceName: updatedDto.serviceName || a.serviceName,
                    type: updatedDto.type || a.type,
                    typeId: updatedDto.typeId || a.typeId,
                    // Preserve amount and discount for correct display calculation
                    amount: updatedDto.amount ?? a.amount,
                    discount: updatedDto.discount ?? a.discount,
                    bookingType: updatedDto.bookingType ?? a.bookingType,
                  }
                : a,
            ),
          );
        }

        toast.success("Appointment updated successfully");

        // If appointment was cancelled, also cancel the associated payment
        if (appointmentData.status === "CANCELLED") {
          try {
            const payment = await paymentService.getPaymentByRefId(id);
            if (payment && payment.status !== "CANCELLED") {
              await paymentService.updatePaymentStatus(payment.id, "CANCELLED");
              toast.success("Associated payment has been cancelled");
            }
          } catch (paymentError) {
            console.error("Error cancelling associated payment:", paymentError);
            // Don't throw - appointment was updated successfully, payment sync can fail silently
          }
          // Trigger billing refresh to reflect payment status change
          window.dispatchEvent(new CustomEvent("billing-refresh"));
        }

        // Trigger billing refresh if vehicles were updated (billing auto-synced by backend)
        if (appointmentData.vehicleIds !== undefined) {
          window.dispatchEvent(new CustomEvent("billing-refresh"));
        }
      } catch (error: any) {
        // Rollback optimistic update
        setAppointments((prev) =>
          prev.map((a) => (a.id === id ? previousAppointment : a)),
        );
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update appointment";
        toast.error(errorMessage);
        throw error;
      }
    },
    [
      appointments,
      vehicles,
      customers,
      subscriptions,
      updateVehicle,
      updateCustomer,
      updateSubscription,
    ],
  );

  const deleteAppointment = useCallback(
    async (id: string) => {
      // Store previous state for rollback
      const previousAppointment = appointments.find((a) => a.id === id);
      if (!previousAppointment) {
        throw new Error("Appointment not found");
      }

      // Optimistic update
      setAppointments((prev) => prev.filter((a) => a.id !== id));

      try {
        await appointmentService.deleteAppointment(id);
        toast.success("Appointment deleted successfully");
      } catch (error: any) {
        // Rollback optimistic update
        setAppointments((prev) => [...prev, previousAppointment]);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to delete appointment";
        toast.error(errorMessage);
        throw error;
      }
    },
    [appointments],
  );

  const getAppointmentById = useCallback(
    (id: string) => {
      return appointments.find((a) => a.id === id);
    },
    [appointments],
  );

  // NOTE: Initial fetch is NOT done here to avoid race condition.
  // AppointmentManagement.tsx calls fetchAppointmentsWithFilters on mount with proper filters.

  // Listen for appointments-refresh events (triggered when payment status is cancelled)
  React.useEffect(() => {
    const handleAppointmentsRefresh = () => {
      console.log("Appointments refresh triggered by payment status update");
      refreshAppointments();
    };
    window.addEventListener("appointments-refresh", handleAppointmentsRefresh);
    return () => {
      window.removeEventListener(
        "appointments-refresh",
        handleAppointmentsRefresh,
      );
    };
  }, [refreshAppointments]);

  const value: AppointmentContextType = {
    appointments,
    loading,
    error,
    pagination,
    addAppointment,
    updateAppointment,
    deleteAppointment,
    getAppointmentById,
    fetchAppointments,
    fetchAppointmentsWithFilters,
    refreshAppointments,
  };

  return (
    <AppointmentContext.Provider value={value}>
      {children}
    </AppointmentContext.Provider>
  );
}

export function useAppointments() {
  const context = useContext(AppointmentContext);
  if (context === undefined) {
    throw new Error(
      "useAppointments must be used within an AppointmentProvider",
    );
  }
  return context;
}
